import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Lock, MessageSquare, Paperclip, Pencil, Send, Trash2 } from 'lucide-react';
import type { MentionableUserList, Note, NoteCreate, NoteList, NoteUpdate } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { ConfirmDialog, Modal, cn } from '../ui';
import { AttachmentsPanel } from './AttachmentsPanel';

/**
 * Notes thread with @mentions (MRLCO task notes, generalised). The mention picker offers only people who can
 * already see the record; mentions notify through the notification centre; a note may be "Only me".
 */
export function NotesThread({ ownerType, ownerId, canModerate }: { ownerType: string; ownerId: string; canModerate: boolean }) {
  const { t, i18n } = useTranslation();
  const { me } = useAuth();
  const qc = useQueryClient();
  const key = ['notes', ownerType, ownerId];
  const notes = useQuery({ queryKey: key, queryFn: () => api.get<NoteList>(`/api/notes?owner_type=${ownerType}&owner_id=${ownerId}`) });
  const mentionable = useQuery({ queryKey: ['notes', 'mentionable', ownerType, ownerId], queryFn: () => api.get<MentionableUserList>(`/api/notes/mentionable?owner_type=${ownerType}&owner_id=${ownerId}`), staleTime: 60_000 });
  const [body, setBody] = useState('');
  const [privateNote, setPrivateNote] = useState(false);
  const [mentions, setMentions] = useState<string[]>([]);
  const [menu, setMenu] = useState<{ query: string; start: number; up: boolean } | null>(null);
  const [editing, setEditing] = useState<Note | null>(null);
  const [editBody, setEditBody] = useState('');
  const [toDelete, setToDelete] = useState<Note | null>(null);
  const [filesFor, setFilesFor] = useState<Note | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const create = useMutation({
    mutationFn: (b: NoteCreate) => api.post<Note>(`/api/notes?owner_type=${ownerType}&owner_id=${ownerId}`, b),
    onSuccess: () => { setBody(''); setMentions([]); setPrivateNote(false); void invalidate(); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const update = useMutation({
    mutationFn: ({ id, b }: { id: string; b: NoteUpdate }) => api.patch<Note>(`/api/notes/${id}`, b),
    onSuccess: () => { setEditing(null); void invalidate(); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/notes/${id}`), onSuccess: () => void invalidate(), onError: (e) => toast.error(describeError(e, t)) });

  const people = mentionable.data?.items ?? [];
  const nameOf = useMemo(() => new Map(people.map((u) => [u.id, pick(i18n.language, u.display_name_en, u.display_name_ar)])), [people, i18n.language]);
  // Either language matches, so an Arabic name is found from an English page and the other way round; the list scrolls, it is not cut short.
  const suggestions = menu ? people.filter((u) => `${u.display_name_en} ${u.display_name_ar}`.toLowerCase().includes(menu.query.toLowerCase())) : [];

  const onChange = (value: string, caret: number) => {
    setBody(value);
    const before = value.slice(0, caret);
    const at = before.lastIndexOf('@');
    if (at >= 0 && (at === 0 || /\s/.test(before[at - 1] ?? '')) && !/\s/.test(before.slice(at + 1))) {
      // The thread usually sits at the foot of the page: open upwards when the picker would fall off the screen.
      const rect = textarea.current?.getBoundingClientRect();
      const below = rect ? window.innerHeight - rect.bottom : Infinity;
      setMenu({ query: before.slice(at + 1), start: at, up: Boolean(rect) && below < 260 && rect!.top > below });
    } else setMenu(null);
  };
  const insertMention = (id: string) => {
    if (!menu) return;
    const name = nameOf.get(id) ?? '';
    const caret = textarea.current?.selectionStart ?? body.length;
    const next = `${body.slice(0, menu.start)}@${name} ${body.slice(caret)}`;
    setBody(next);
    setMentions((m) => (m.includes(id) ? m : [...m, id]));
    setMenu(null);
    textarea.current?.focus();
  };
  const submit = () => {
    if (!body.trim()) return;
    // Keep only mentions whose @Name is still in the text.
    const kept = mentions.filter((id) => body.includes(`@${nameOf.get(id) ?? ''}`));
    create.mutate({ body: body.trim(), mentions: kept, visibility: privateNote ? 'private' : 'entity' });
  };

  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><MessageSquare size={16} className="text-neutral-500" />{t('notes.title')}{notes.data && <span className="text-xs font-normal text-neutral-500">({notes.data.items.length})</span>}</div>
      {notes.data && notes.data.items.length === 0 && <p className="mb-3 text-sm text-neutral-500">{t('notes.empty')}</p>}
      <ul className="mb-4 grid gap-3">
        {(notes.data?.items ?? []).map((n) => {
          const mine = n.author_id === me?.id;
          return (
            <li key={n.id} className={cn('rounded-xl px-3 py-2 text-sm', mine ? 'ms-8 bg-brand-50 dark:bg-brand-700/20' : 'me-8 bg-neutral-100 dark:bg-neutral-800')}>
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                <span className="font-medium text-neutral-700 dark:text-neutral-300" dir="auto">{pick(i18n.language, n.author_name_en, n.author_name_ar) || t('audit.system')}</span>
                <span>{new Date(n.created_at).toLocaleString(i18n.language)}</span>
                {n.edited_at && <span>· {t('notes.edited')}</span>}
                {n.visibility === 'private' && <span className="inline-flex items-center gap-1"><Lock size={10} /> {t('records.onlyMe')}</span>}
                <span className="ms-auto flex gap-1">
                  <button type="button" className="rounded p-1 hover:bg-white/60 dark:hover:bg-neutral-700" onClick={() => setFilesFor(n)} aria-label={t('attachments.title')}><Paperclip size={12} />{n.attachment_count > 0 && <span className="ms-0.5">{n.attachment_count}</span>}</button>
                  {(mine || canModerate) && <button type="button" className="rounded p-1 hover:bg-white/60 dark:hover:bg-neutral-700" onClick={() => { setEditing(n); setEditBody(n.body); }} aria-label={t('app.edit')}><Pencil size={12} /></button>}
                  {(mine || canModerate) && <button type="button" className="rounded p-1 hover:bg-white/60 dark:hover:bg-neutral-700" onClick={() => setToDelete(n)} aria-label={t('app.delete')}><Trash2 size={12} /></button>}
                </span>
              </div>
              <div className="whitespace-pre-wrap" dir="auto">{n.body}</div>
            </li>
          );
        })}
      </ul>
      <div>
        <div className="relative">
          <textarea ref={textarea} className="input block min-h-20" placeholder={t('notes.placeholder')} value={body} onChange={(e) => onChange(e.target.value, e.target.selectionStart)} onBlur={() => setMenu(null)} onKeyDown={(e) => { if (e.key === 'Escape') setMenu(null); if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') submit(); }} dir="auto" />
          {menu && mentionable.data && (
            <div className={cn('card absolute z-30 w-72 max-w-full overflow-hidden text-sm shadow-lg', menu.up ? 'bottom-full mb-1' : 'top-full mt-1')} onMouseDown={(e) => e.preventDefault()}>
              <ul className="max-h-56 overflow-y-auto py-1">
                {suggestions.length === 0 && <li className="px-3 py-2 text-neutral-500">{t('notes.mentionNone')}</li>}
                {suggestions.map((u) => (
                  <li key={u.id}><button type="button" className="flex w-full items-center gap-2 px-3 py-1.5 text-start hover:bg-neutral-100 dark:hover:bg-neutral-800" onMouseDown={(e) => { e.preventDefault(); insertMention(u.id); }}><span className="h-5 w-5 shrink-0 overflow-hidden rounded-full bg-neutral-300">{u.avatar_url && <img src={u.avatar_url} alt="" className="h-full w-full object-cover" />}</span><span className="truncate" dir="auto">{pick(i18n.language, u.display_name_en, u.display_name_ar)}</span></button></li>
                ))}
              </ul>
              <p className="border-t border-neutral-100 px-3 py-1.5 text-[11px] text-neutral-500 dark:border-neutral-800">{t('notes.mentionHint')}</p>
            </div>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-xs text-neutral-500"><input type="checkbox" checked={privateNote} onChange={(e) => setPrivateNote(e.target.checked)} /><Lock size={12} /> {t('notes.onlyMe')}</label>
          <button type="button" className="btn-primary" disabled={!body.trim() || create.isPending} onClick={submit}><Send size={14} /> {t('notes.post')}</button>
        </div>
      </div>
      <Modal open={editing !== null} onClose={() => setEditing(null)} title={t('notes.editTitle')} footer={<><button type="button" className="btn-secondary" onClick={() => setEditing(null)}>{t('app.cancel')}</button><button type="button" className="btn-primary" disabled={!editBody.trim() || update.isPending} onClick={() => editing && update.mutate({ id: editing.id, b: { body: editBody.trim() } })}>{t('app.save')}</button></>}>
        <textarea className="input min-h-32" value={editBody} onChange={(e) => setEditBody(e.target.value)} dir="auto" />
      </Modal>
      <Modal open={filesFor !== null} onClose={() => { setFilesFor(null); void invalidate(); }} title={t('attachments.title')}>
        {filesFor && <AttachmentsPanel ownerType="note" ownerId={filesFor.id} canEdit={filesFor.author_id === me?.id || canModerate} compact />}
      </Modal>
      <ConfirmDialog open={toDelete !== null} title={t('notes.deleteTitle')} danger onCancel={() => setToDelete(null)} onConfirm={() => { if (toDelete) remove.mutate(toDelete.id); setToDelete(null); }} />
    </div>
  );
}
